import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import get from 'lodash/get'
import './checkout-total-price.scss'
import DateFormat from '../helpers/DateFormat'
import API from '../../api'
import { useState, useEffect, Fragment } from 'react'
import { setTotalPrice } from '../../actions/adventure-checkout-actions'
import { AdventureOriginalTitle } from '../adventures'
import store2 from 'store2'
import { trackGA4Ecommerce } from '../../gtm'

import PropTypes from 'prop-types'
import { parseQuery } from '../../utils'
import CalculatedPrice from './CalculatedPrice'
import styles from './CalculatedPrice.module.scss'
import { convertCurrency } from '../helpers'

function CheckoutTotalPrice({
  shop,
  activity,
  activityDateTime,
  activityId,
  extraOptions,
  participants,
  inclusions,
  totalPrice,
  setTotalPrice,
  toBePaidOffline,
  selectedCurrency,
}) {
  const [isLoading, setLoading] = useState(true)
  const { fromDiveSite: diveSiteTitle } = window
  const activityDate = activityDateTime.split(' ')?.[0]
  const activityStartTime = activityDateTime.split(' ')?.[1]
  const query = parseQuery() || {}
  const { chargePrice } = totalPrice

  useEffect(() => {
    API('/api/adventure/v1/checkout/orders/calculate-price/')
      .post([
        {
          date: activityDate,
          activityId,
          quantity: participants,
          additionalExtraOptions: extraOptions,
          currency_code: query.currency_code || selectedCurrency,
        },
      ])
      .then((price) => {
        if(price.chargePrice) {
          setTotalPrice(price)
          trackGA4Ecommerce(
            'begin_checkout',
            {
              ...store2.session('ga4Ecommerce'),
              value: Number(convertCurrency(Number(price?.originalPrice?.value), price?.originalPrice?.currency, 'USD').toFixed(2)),
            },
            'PADI Travel - Adventures',
          )
          store2.session('ga4Cart', {
            ecommerce: { ...store2.session('ga4Ecommerce'), value: Number(price?.chargePrice?.value).toFixed(2) },
          })
        }
      })
      .finally(() => setLoading(false))
  }, [activityDate])

  const activityPicture = get(activity, 'allImages[0].image.x1080')
  const diveSiteNotGuaranteed =
    activity.diveSiteType && activity.diveSiteType.value === 10
  const isFree = parseFloat(totalPrice.value) === 0
  return (
    <div className='total-price-section'>
      <p className='section-title'>
        <FormattedMessage
          id='your_order_details'
          defaultMessage='Your order details'
        />
      </p>
      <div className='shadow-block sticky-section form-section'>
        <img src={activityPicture} className='activity-picture' />
        <p className='activity-title'>
          {activity.title}
          <AdventureOriginalTitle
            title={activity.title}
            originalTitle={activity.originalTitle}
          />
        </p>
        <p className='shop-title'>{shop.title}</p>
        {diveSiteTitle && (
          <p>
            <i className='font-icons icon-dive-sites' />
            {diveSiteTitle}
            {diveSiteNotGuaranteed && (
              <span>
                &nbsp; (
                <FormattedMessage
                  id='not_guaranteed'
                  defaultMessage='not guaranteed'
                />
                )
              </span>
            )}
          </p>
        )}
        <div className='total-price-section__info no-border-bottom'>
          <div className='total-price-section__info__row'>
            <span className='info-title'>
              <FormattedMessage id='date' defaultMessage='Date' />
            </span>
            <span className='info-details'>
              <DateFormat date={activityDate} />
            </span>
          </div>
          <div className='total-price-section__info__row'>
            <span className='info-title'>
              <FormattedMessage id='time' defaultMessage='Time' />
            </span>
            <span className='info-details'>
              <FormattedMessage
                id='departure_time'
                defaultMessage='{time} departure'
                values={{ time: activityStartTime }}
              />
            </span>
          </div>
        </div>
        <div className='total-price-section__info'>
          <div className='total-price-section__info__row'>
            <span className='info-title'>
              <FormattedMessage id='quantity' defaultMessage='Quantity' />
            </span>
            <span className='info-details'>
              <FormattedMessage
                id='participants_quantity'
                values={{ qty: participants }}
                defaultMessage='{ qty, plural, one {# x Participant} other {# x Participants}}'
              />
            </span>
          </div>
          {!!inclusions.length && (
            <div className='total-price-section__info__row'>
              <span className='info-title'>
                <FormattedMessage id='extras' defaultMessage='Extras' />
              </span>
              <span className='info-details'>
                {inclusions.map((inclusion) => (
                  <p className='mbn' key={inclusion.title}>
                    {inclusion.quantity} x {inclusion.title}
                  </p>
                ))}
              </span>
            </div>
          )}
        </div>
        <div className='total-price'>
          <span className='total'>
            <FormattedMessage id='total' defaultMessage='Total' />
          </span>
          {isLoading ? (
            <FormattedMessage
              id='calculating'
              defaultMessage='Calculating...'
            />
          ) : (
            <CalculatedPrice
              totalPrice={totalPrice}
              selectedCurrency={selectedCurrency}
            />
          )}
        </div>
        {chargePrice?.currency !== selectedCurrency && (
          <Fragment>
            <br />
            <span className={styles.convertInfo}>
              <FormattedMessage
                id='checkout.last.step.price_hint1'
                values={{
                  currency: chargePrice?.currency,
                }}
                defaultMessage='This conversion is an approximate price based on the current exchange rate and may change before your stay. You will have to pay in {currency}'
              />
            </span>
          </Fragment>
        )}
        {toBePaidOffline && !isFree && (
          <div className='warning-message mts mbn'>
            <FormattedMessage
              id='fee.activity'
              defaultMessage='The fee for this activity is to be paid onsite directly with the organiser'
            />
          </div>
        )}
      </div>
      {activity.optionsBlock && (
        <p className='free-cancellation'>
          {activity.optionsBlock.cancellationPolicy}
        </p>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  totalPrice: state.totalPrice,
  selectedCurrency: state.selectedCurrency,
})

export default connect(mapStateToProps, { setTotalPrice })(CheckoutTotalPrice)

CheckoutTotalPrice.propTypes = {
  shop: PropTypes.object,
  activity: PropTypes.object,
  activityDateTime: PropTypes.string,
  activityId: PropTypes.string,
  extraOptions: PropTypes.array,
  participants: PropTypes.number,
  inclusions: PropTypes.array,
  totalPrice: PropTypes.object,
  setTotalPrice: PropTypes.func,
  toBePaidOffline: PropTypes.bool,
  selectedCurrency: PropTypes.string,
}
